import { useEffect, useMemo, useState } from 'react';
import {
    buildDomain,
    centreViewport,
    ensureVisible,
    panViewport,
    resizeViewport,
    zoomViewportAt,
    type ScaleDomain,
    type TimelineEntry
} from './scanScale';

export interface Viewport {
    full: ScaleDomain;
    viewport: ScaleDomain;
    pan: (deltaMs: number) => void;
    centreOn: (time: number) => void;
    /** Scales the span about a fixed instant — the one under the cursor. */
    zoomAt: (time: number, factor: number) => void;
    fit: () => void;
    resize: (edge: 'start' | 'end', time: number) => void;
}

/** Scoped range state; null means the full range, so new data never leaves a stale window. */
export function useViewport(
    entries: TimelineEntry[],
    now: number,
    selectedTime: number | null
): Viewport {
    const [scoped, setScoped] = useState<ScaleDomain | null>(null);
    const full = useMemo(() => buildDomain(entries, now), [entries, now]);
    const viewport = scoped ?? full;

    useEffect(() => {
        if (selectedTime == null) return;
        setScoped((current) => (current ? ensureVisible(current, full, selectedTime) : current));
    }, [full, selectedTime]);

    return {
        full,
        viewport,
        // Updater form throughout: a drag fires many pointermove events per commit, and a
        // value-form pan would recompute from a stale viewport and drop deltas.
        pan: (deltaMs: number) => setScoped((prev) => panViewport(prev ?? full, full, deltaMs)),
        centreOn: (time: number) => setScoped((prev) => centreViewport(prev ?? full, full, time)),
        zoomAt: (time: number, factor: number) =>
            setScoped((prev) => {
                const next = zoomViewportAt(prev ?? full, full, time, factor);
                // Back at the full range, drop the scope so later data widens the
                // window instead of pinning it to today's extent.
                return next.end - next.start >= full.end - full.start ? null : next;
            }),
        fit: () => setScoped(null),
        resize: (edge, time) => setScoped((prev) => resizeViewport(prev ?? full, full, edge, time))
    };
}
